import { useState } from "react";

import * as service from "../services/memberGroupsService";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const mergeUniqueMembers = (current, incoming) => {
  const members = new Map();

  [...current, ...incoming].forEach((member) => {
    if (member?.memberId != null) {
      members.set(String(member.memberId), member);
    }
  });

  return Array.from(members.values());
};

export const useMemberGroups = () => {
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState(null);
  const [membership, setMembership] = useState(null);

  const [approvedMembers, setApprovedMembers] = useState([]);
  const [approvedMemberCount, setApprovedMemberCount] = useState(0);

  const [hasGroupUpdates, setHasGroupUpdates] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const findGroupCode = async (groupCode) => {
    const code = String(groupCode ?? "").trim();

    if (!code) {
      setError("Group code is required.");
      return false;
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.findGroupCode(code);
      const data = response?.data ?? null;

      setGroup(data);

      return data;
    } catch (requestError) {
      setGroup(null);
      setError(getErrorMessage(requestError, "Invalid group code."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getMemberGroups = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await service.getMemberGroups();

      const data = Array.isArray(response?.data) ? response.data : [];

      setGroups(data);

      return data;
    } catch (requestError) {
      setGroups([]);
      setError(getErrorMessage(requestError, "Failed to load groups."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getMemberGroup = async (groupTag) => {
    const tag = String(groupTag ?? "").trim();

    if (!tag) {
      setGroup(null);
      setError("Group tag is required.");
      return false;
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.getMemberGroup(tag);
      const data = response?.data ?? null;

      if (!data?.groupTag) {
        throw new Error("Invalid group response.");
      }

      if (data.canView === false) {
        throw new Error("You do not have access to this group.");
      }

      setGroup(data);

      setMembership(
        data.isOwner || !data.memberId
          ? null
          : {
              memberId: data.memberId,
              groupTag: data.groupTag,
              status: data.memberStatus,
            },
      );

      return data;
    } catch (requestError) {
      setGroup(null);
      setMembership(null);
      setError(getErrorMessage(requestError, "Failed to load group."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const joinGroup = async (groupTag, reasonForJoining = "") => {
    const tag = String(groupTag ?? "").trim();
    const reason = String(reasonForJoining ?? "").trim();

    if (!tag || !reason) {
      setError(
        !tag ? "Group tag is required." : "Reason for joining is required.",
      );
      return false;
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.joinGroup(tag, {
        reasonForJoining: reason,
      });

      return response?.data ?? true;
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Failed to send join request."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const getApprovedGroupMemberships = async ({
    groupTag,
    limit = 50,
    offset = 0,
  }) => {
    if (!groupTag) return false;

    if (offset > 0) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const response = await service.getApprovedGroupMemberships({
        groupTag,
        limit,
        offset,
      });

      const data = response?.data ?? [];

      const members = Array.isArray(data)
        ? data
        : Array.isArray(data.members)
          ? data.members
          : Array.isArray(data.memberships)
            ? data.memberships
            : [];

      setApprovedMembers((current) =>
        offset > 0 ? mergeUniqueMembers(current, members) : members,
      );

      return data;
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "Failed to load group memberships."),
      );

      return false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const getApprovedGroupMembershipsCount = async (groupTag) => {
    if (!groupTag) return false;

    try {
      const response = await service.getApprovedGroupMembershipsCount(groupTag);

      const data = response?.data ?? {};
      const count = Number(data.approvedMemberCount ?? 0);

      setApprovedMemberCount(Number.isFinite(count) && count >= 0 ? count : 0);

      return data;
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "Failed to load group member count."),
      );

      return false;
    }
  };

  const updateMemberGroupMembership = async (groupTag, reasonForJoining) => {
    const tag = String(groupTag ?? "").trim();
    const reason = String(reasonForJoining ?? "").trim();

    if (!tag || !reason) {
      setError("Group tag and reason are required.");
      return false;
    }

    setLoading(true);
    setError("");

    try {
      const response = await service.updateMemberGroupMembership(tag, {
        reasonForJoining: reason,
      });

      setMembership((current) =>
        current
          ? {
              ...current,
              reasonForJoining: reason,
            }
          : current,
      );

      return response?.data ?? true;
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Failed to update membership."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const leaveMemberGroup = async (groupTag) => {
    const tag = String(groupTag ?? "").trim();

    if (!tag) return false;

    setLoading(true);
    setError("");

    try {
      const response = await service.leaveMemberGroup(tag);

      setGroups((current) =>
        current.filter((item) => String(item.groupTag) !== tag),
      );

      setGroup((current) =>
        String(current?.groupTag) === tag ? null : current,
      );

      setMembership((current) =>
        String(current?.groupTag) === tag ? null : current,
      );

      return response?.data ?? true;
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Failed to leave group."));

      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateMemberGroupLastViewed = async (groupTag) => {
    const tag = String(groupTag ?? "").trim();

    if (!tag) return false;

    try {
      const response = await service.updateMemberGroupLastViewed(tag);

      setGroups((current) =>
        current.map((item) =>
          String(item.groupTag) === tag
            ? {
                ...item,
                hasUpdates: false,
              }
            : item,
        ),
      );

      return response?.data ?? true;
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "Failed to update group activity."),
      );

      return false;
    }
  };

  const checkGlobalGroupUpdates = async () => {
    try {
      const response = await service.checkMemberGroupUpdates();

      const updated = Boolean(response?.data?.hasGroupUpdates);

      setHasGroupUpdates(updated);

      return updated;
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Failed to check group updates."));

      return false;
    }
  };

  const resetMemberGroups = () => {
    setGroups([]);
    setGroup(null);
    setMembership(null);

    setApprovedMembers([]);
    setApprovedMemberCount(0);

    setHasGroupUpdates(false);

    setLoading(false);
    setLoadingMore(false);
    setError("");
  };

  return {
    groups,
    group,
    membership,

    approvedMembers,
    approvedMemberCount,

    hasGroupUpdates,

    loading,
    loadingMore,
    error,

    setGroups,
    setGroup,
    setMembership,

    setApprovedMembers,
    setApprovedMemberCount,
    setError,

    findGroupCode,
    getMemberGroups,
    getMemberGroup,

    joinGroup,
    leaveMemberGroup,

    getApprovedGroupMemberships,
    getApprovedGroupMembershipsCount,

    updateMemberGroupMembership,
    updateMemberGroupLastViewed,

    checkGlobalGroupUpdates,
    resetMemberGroups,
  };
};

export default useMemberGroups;
